<template>
  <app-modal
    v-if="show"
    :maxWidth="maxWidth"
    :maxHeight="maxHeight"
    :isClickMaskClose="false"
    @close="hideModal"
  >
    <el-form
      class="attribute-templates-form"
      ref="attributeTemplatesForm"
      :rules="rules"
      :model="attributeForm"
      :validate-on-rule-change="false"
    >
      <h3 class="attribute-templates-form__title">
        {{ title }}
      </h3>

      <el-form-item
        class="attribute-templates-form__item"
        :label="$t('attribute_templates_type')"
        prop="type"
      >
        <el-select v-model="attributeForm.type" :disabled="isEdit" filterable>
          <el-option
            v-for="item in attributeTypes"
            :key="item.type"
            :label="item.name"
            :value="item.type"
          ></el-option>
        </el-select>
      </el-form-item>

      <el-form-item
        v-if="isSetSize"
        class="attribute-templates-form__item"
        :label="$t('attribute_templates_string_size')"
        prop="size"
      >
        <el-input-number
          v-model="attributeForm.size"
          :disabled="isEdit"
          :min="1"
          :max="maxSize"
        ></el-input-number>
      </el-form-item>

      <el-form-item
        v-if="isLookup"
        class="attribute-templates-form__item"
        :label="$t('attribute_templates_lookup')"
        prop="lookup"
      >
        <el-select v-model="attributeForm.lookup" :disabled="isEdit" filterable>
          <el-option
            v-for="item in references"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          ></el-option>
        </el-select>
      </el-form-item>

      <el-form-item
        v-if="isLookup"
        class="attribute-templates-form__item"
        prop="multiSelect"
      >
        <el-checkbox v-model="attributeForm.multiSelect" :disabled="isEdit">
          {{ $t("attribute_templates_lookup_multy") }}
        </el-checkbox>
      </el-form-item>

      <el-form-item
        class="attribute-templates-form__item"
        :label="$t('attribute_templates_attr_code')"
        prop="code"
      >
        <el-input
          class="show-word-limit-pad"
          v-model="attributeForm.code"
          :disabled="isEdit"
          maxlength="20"
          show-word-limit
          autofocus
        ></el-input>
      </el-form-item>

      <el-form-item class="attribute-templates-form__item">
        <el-checkbox
          v-model="attributeForm.noTranslationRequired"
          :disabled="noTranslationRequiredLock"
          :title="$t('attribute_templates_attr_no_translation_hint')"
        >
          {{ $t("attribute_templates_attr_no_translation") }}
        </el-checkbox>
      </el-form-item>

      <el-form-item
        class="attribute-templates-form__item"
        v-for="lang in languages"
        :key="lang.id"
        :label="$t('attribute_templates_attr_name', { name: lang.name })"
        :prop="lang.id"
      >
        <el-input
          class="show-word-limit-pad"
          v-model="attributeForm[lang.id]"
          maxlength="100"
          show-word-limit
        >
          <el-button
            v-if="!lang.default"
            slot="append"
            :title="$t('system_translate')"
            icon="el-icon-magic-stick"
          ></el-button>
        </el-input>
      </el-form-item>

      <el-form-item
        class="attribute-templates-form__item"
        :label="$t('attribute_templates_comment')"
        prop="comment"
      >
        <el-input
          class="show-word-limit-pad"
          v-model="attributeForm.comment"
          maxlength="255"
          show-word-limit
        ></el-input>
      </el-form-item>

      <el-form-item
        class="attribute-templates-form__item attribute-templates-form__action"
      >
        <el-button @click="hideModal">
          {{ $t("attribute_templates_cancel") }}
        </el-button>

        <el-button type="success" @click="submitForm('attributeTemplatesForm')">
          {{ $t("attribute_templates_save") }}
        </el-button>
      </el-form-item>
    </el-form>
  </app-modal>
</template>

<script>
import {
  STRING,
  INTEGER,
  NUMBER,
  DATE,
  COORDINATE,
  GALLERY,
  FILE,
  PROGRAM,
  METADATA,
  LOOKUP
} from "@/domain/data.types";

export default {
  name: "AttributeTemplatesForm",
  components: {
    AppModal: () =>
      import(
        /* webpackChunkName: "appModal" */ "@/components/library.blocks/AppModal.vue"
      )
  },
  props: {
    maxWidth: {
      type: String,
      default: "100%"
    },
    maxHeight: {
      type: String,
      default: "100%"
    },
    show: {
      type: Boolean,
      default: false
    },
    languages: Array,
    attributeTypes: Array,
    references: Array,
    attribute: Object
  },
  data() {
    return {
      maxSize: 2000,
      attributeForm: {},
      rules: {}
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.attribute);
    },
    title() {
      return this.isEdit
        ? this.$t("attribute_templates_edit_title")
        : this.$t("attribute_templates_new_title");
    },
    isString() {
      return this.attributeForm.type === STRING;
    },
    isLookup() {
      return this.attributeForm.type === LOOKUP;
    },
    noTranslationRequiredGet() {
      return [
        INTEGER,
        NUMBER,
        DATE,
        COORDINATE,
        LOOKUP,
        GALLERY,
        FILE
      ].includes(this.attributeForm.type);
    },
    isSetSize() {
      return [STRING, METADATA].includes(this.attributeForm.type);
    },
    noTranslationRequiredLock() {
      return (
        this.isEdit ||
        [
          INTEGER,
          NUMBER,
          DATE,
          COORDINATE,
          LOOKUP,
          GALLERY,
          PROGRAM,
          METADATA,
          GALLERY,
          FILE
        ].includes(this.attributeForm.type)
      );
    }
  },
  methods: {
    initLocalState() {
      this.setNameProps();
      this.setAtributeValues();
      this.reset();
      this.setRuleName();
    },
    setNameProps() {
      let attributeForm = {
        code: "",
        noTranslationRequired: this.noTranslationRequiredGet,
        type: "",
        size: this.maxSize,
        lookup: undefined,
        multiSelect: false,
        comment: ""
      };

      this.languages.forEach(lang => {
        if (!attributeForm[lang.id]) {
          attributeForm[lang.id] = "";
        }
      });

      this.attributeForm = attributeForm;
    },
    getRulesPreset() {
      return {
        code: [
          {
            required: true,
            message: this.$t("attribute_templates_code_rule"),
            trigger: "change"
          }
        ],
        type: [
          {
            required: true,
            message: this.$t("attribute_templates_code_type"),
            trigger: "change"
          }
        ],
        size: [
          {
            required: true,
            message: this.$t("attribute_templates_size_rule"),
            trigger: "change"
          }
        ],
        lookup: [
          {
            required: true,
            message: this.$t("attribute_templates_lookup_rule"),
            trigger: "change"
          }
        ]
      };
    },
    setRuleName() {
      let rule = {};
      this.languages
        .filter(lang => lang.default)
        .forEach(lang => {
          rule[lang.id] = [
            {
              required: true,
              message: this.$t("attribute_templates_name_rule"),
              trigger: "change"
            }
          ];
        });
      let rules = { ...this.getRulesPreset(), ...rule };
      this.rules = rules;
    },
    setAtributeValues() {
      if (this.isEdit) {
        this.attributeForm = { ...this.attributeForm, ...this.attribute };
      }
    },
    hideModal() {
      this.$emit("close");
    },
    save() {
      this.$emit("save", this.attributeForm);
    },
    submitForm(formName) {
      this.$refs[formName].validate(valid => {
        if (valid) {
          this.save();
          this.initLocalState();
          this.hideModal();
        } else {
          return false;
        }
      });
    },
    reset() {
      if (this.$refs.attributeTemplatesForm !== undefined) {
        this.$refs.attributeTemplatesForm.resetFields();
      }
    }
  },
  mounted() {
    this.initLocalState();
  },
  watch: {
    languages: function () {
      this.setNameProps();
      this.setRuleName();
    },
    attribute: function () {
      this.setAtributeValues();
    },
    "attributeForm.type": function () {
      if (!this.isEdit) {
        this.attributeForm.noTranslationRequired = this.noTranslationRequiredGet;
      }
    }
  }
};
</script>

<style lang="scss">
@import "@/css/variables.scss";

.attribute-templates-form {
  .attribute-templates-form__title {
    text-align: left;
    padding: $form_title_padding;
  }

  .attribute-templates-form__item {
    padding: $form_item_padding;
  }

  .attribute-templates-form__action {
    text-align: right;
  }
}
</style>
